import { dehydrate, hydrate, type DehydratedState, type Query, type QueryClient, type QueryKey } from '@tanstack/react-query';
import type { AsyncKeyValueStore } from './indexedDbStore';

export const QUERY_CACHE_KEY = 'volley-query-cache';
/** Older than this, a team or a roster may have changed too much to show offline. */
export const QUERY_CACHE_MAX_AGE_MS = 3 * 24 * 60 * 60_000;
const SAVE_THROTTLE_MS = 2_000;
/** Roots of the kept reads (see `isPersistedQuery`). */
const PERSISTED_ROOTS = ['teams', 'team', 'matches', 'opponent-teams'] as const;

interface PersistedQueryCache {
  userId: string;
  savedAt: number;
  state: DehydratedState;
}

/**
 * Reads kept for offline use (VOL-14): teams, their details and rosters,
 * matches and opponent teams. Stats, videos and imports are not: they are
 * large, and only make sense fresh.
 */
export function isPersistedQuery(query: { queryKey: QueryKey; state: Pick<Query['state'], 'status'> }): boolean {
  if (query.state.status !== 'success') return false;
  const [root, second, third] = query.queryKey;
  switch (root) {
    case 'teams':
    case 'opponent-teams':
      return true;
    case 'team':
      return third === undefined || third === 'players';
    case 'matches':
      // ['matches', season] and ['matches', id]; not ['matches', 'import', …] nor a match's sub-lists.
      return second !== 'import' && third === undefined;
    default:
      return false;
  }
}

export interface QueryPersistenceOptions {
  client: QueryClient;
  store: AsyncKeyValueStore | null;
  /** The signed-in user: another account never gets these reads back. */
  userId: () => string | null;
  now?: () => number;
}

export interface QueryPersistence {
  /** Saves the kept reads, a moment after each one changes. */
  start: () => void;
  /** Puts the reads kept last time back in the cache; never newer data over older. */
  restore: () => Promise<void>;
  /** True once a `restore` has settled, whatever it found: a missing read is then really missing. */
  isRestored: () => boolean;
  onRestored: (listener: () => void) => () => void;
  /** Drops the kept reads (sign-out). */
  clear: () => Promise<void>;
  /** Stops saving. */
  stop: () => void;
  /** Saves now (tests, sign-out). */
  save: () => Promise<void>;
}

export function createQueryPersistence(options: QueryPersistenceOptions): QueryPersistence {
  const { client, store } = options;
  const now = options.now ?? Date.now;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const save = async (): Promise<void> => {
    const userId = options.userId();
    if (!store || !userId) return;
    const state = dehydrate(client, { shouldDehydrateQuery: isPersistedQuery });
    const payload: PersistedQueryCache = { userId, savedAt: now(), state };
    try {
      await store.write([[QUERY_CACHE_KEY, JSON.stringify(payload)]]);
    } catch {
      // A read cache: losing it only means fetching again once online.
    }
  };

  let unsubscribe: (() => void) | null = null;
  let restored = false;
  const restoredListeners = new Set<() => void>();

  const restoreKept = async (): Promise<void> => {
    if (!store) return;
    const userId = options.userId();
    if (!userId) return;
    let payload: PersistedQueryCache | null = null;
    try {
      const raw = await store.get(QUERY_CACHE_KEY);
      payload = raw ? (JSON.parse(raw) as PersistedQueryCache) : null;
    } catch {
      return;
    }
    if (!payload || payload.userId !== userId || !Array.isArray(payload.state?.queries)) return;
    if (now() - payload.savedAt > QUERY_CACHE_MAX_AGE_MS) return;
    // Each save rewrites the whole cache: the age limit is per read, from when
    // it was last fetched, or a roster seen weeks ago would come back.
    const state = payload.state;
    const fresh = {
      ...state,
      queries: state.queries.filter((q) => now() - q.state.dataUpdatedAt <= QUERY_CACHE_MAX_AGE_MS),
    };
    // Restored with their original date: past their staleTime, they refetch
    // as soon as a screen uses them online.
    hydrate(client, fresh);
  };

  return {
    start() {
      if (!store || unsubscribe) return;
      // Each save writes what the cache holds: with the default 5-minute gcTime, a
      // team read then left would drop out of memory, then out of storage at the
      // next save. Set before `restore`, so restored reads get it too. Defaults
      // match by prefix: a match's small sub-lists (views, favorites) stay too.
      for (const root of PERSISTED_ROOTS) {
        client.setQueryDefaults([root], { gcTime: QUERY_CACHE_MAX_AGE_MS });
      }
      unsubscribe = client.getQueryCache().subscribe((event) => {
        if (event.type !== 'updated' || event.action.type !== 'success') return;
        if (!isPersistedQuery(event.query) || timer) return;
        timer = setTimeout(() => {
          timer = null;
          void save();
        }, SAVE_THROTTLE_MS);
      });
    },
    async restore() {
      try {
        await restoreKept();
      } finally {
        restored = true;
        restoredListeners.forEach((listener) => listener());
      }
    },
    isRestored: () => restored,
    onRestored(listener) {
      restoredListeners.add(listener);
      return () => {
        restoredListeners.delete(listener);
      };
    },
    async clear() {
      if (timer) clearTimeout(timer);
      timer = null;
      try {
        await store?.write([[QUERY_CACHE_KEY, null]]);
      } catch {
        // Ignored: the next user check refuses this cache anyway.
      }
    },
    stop() {
      unsubscribe?.();
      unsubscribe = null;
      if (timer) clearTimeout(timer);
      timer = null;
    },
    save,
  };
}
